import { getAPIErrorReason, isEmpty } from '../helpers/common';
import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import {
  ChatGroupInterface,
  SendMessageDetailsInterface,
  UpdateChatGroupOrderPayload,
  UpdateDiscussionMessagePayload,
  UpdateGroupNameInterface,
  UsersChatGroupInterface
} from '../interfaces/chatMessageInterface';
import { captureException } from './logService';
import { rootStore } from '../mobx/rootStore';
import { dispatchChatMessageError, dispatchChatMessageSuccess } from './chatMessageUtilService';

/**
 * @desc Group - create message group
 * @param {*}
 */
export const createMessageGroup = async (payload: {
  userId: string;
  name: string;
  companyId: string;
  users: string[];
}) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/discussions/createMessageGroup`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    captureException(e);
    dispatchChatMessageError(getAPIErrorReason(e) || 'Unable to create group please try again');
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
/**
* @desc Group - update message group
 * @param {*}
 */
export const updateMessageGroup = async (
  groupId: string | undefined,
  payload: UpdateGroupNameInterface | UsersChatGroupInterface
): Promise<ChatGroupInterface | undefined> => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const response = await axios.post(
      `${REACT_APP_TEAMCAMP_APIURL}/discussions/updateMessageGroup/${groupId}`,
      payload
    );
    const { data } = response;
    if (data) {
      dispatchChatMessageSuccess('Group name updated successfully!');
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update group please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Update discussion message with various payload types
 * @param {string} Message_Id - The ID of the message to update
 * @param {boolean | UpdateDiscussionMessagePayload} payload - The payload to update the message with
 */
export const updateDiscussionMessageDetail = async (
  Message_Id: string,
  payload: boolean | UpdateDiscussionMessagePayload
) => {
  try {
    rootStore.chatMessageStore.setLoading(true);

    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/discussions/updateDiscussionData/${Message_Id}`,
      payload
    );
    if (response) {
      return response;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update message please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Message - send discussion messgae
 * @param {*}
 */
export const sendDiscussionMessage = async (payload: SendMessageDetailsInterface) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/discussions/addDiscussionData`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to send message please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Group - add members message group
 * @param {*}
 */
export const addMembersInMessageGroup = async (groupId: string | undefined, payload: UsersChatGroupInterface) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/discussions/addMemberInGroup/${groupId}`, payload);
    const { data } = response;
    if (data) {
      dispatchChatMessageSuccess(data.message);
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add member in group please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Group - add members message group
 * @param {*}
 */
export const removeMemberFromMessageGroup = async (
  groupId: string | undefined,
  userId: string,
  removeUserId: string
) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/discussions/removeGroupMember/${groupId}`, {
      params: { userId, removeUserId }
    });
    const { data } = response;
    if (data) {
      dispatchChatMessageSuccess(data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove member from message group';
    captureException(error);
    dispatchChatMessageError(error);
    return false;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Message - delete group message
 * @param {*}
 */
export const deleteGroupMessage = async (type: number | undefined, groupId: string | undefined) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/discussions/deleteMessageGroup/${groupId}?Type=${type}`
    );
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to archive message please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return false;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/*
 * @desc pin massage group
 * @param {*}
 */
export const pinMessageGroup = async (message_group_id: string) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    if (!isEmpty(message_group_id)) {
      const order = new Date().getTime();
      const response = await axios.post(
        `${REACT_APP_TEAMCAMP_APIURL}/discussions/pinMessageGroup/${message_group_id}`,
        {
          order
        }
      );
      const { data } = response;
      if (data) {
        return data;
      }
    } else {
      dispatchChatMessageError('Message group id is empty');
      return;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to pin group please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc unpin massage group
 * @param {*}
 */
export const unPinMessageGroup = async (message_group_id: string) => {
  try {
    rootStore.chatMessageStore.setLoading(true);
    if (!isEmpty(message_group_id)) {
      const response = await axios.delete(
        `${REACT_APP_TEAMCAMP_APIURL}/discussions/unpinMessageGroup/${message_group_id}`
      );
      if (response) {
        return true;
      }
    } else {
      dispatchChatMessageError('Message group id is empty');
      return;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to unpin group please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};

/**
 * @desc Message - update discussion messgae
 * @param {*}
 */
export const updateChatGroupOrder = async (Message_Id: string, payload: UpdateChatGroupOrderPayload) => {
  try {
    rootStore.chatMessageStore.setLoading(true);

    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/discussions/updatePinnedMessageGroup/${Message_Id}`,
      payload
    );
    if (response) {
      return response;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update chat group please try again';
    captureException(error);
    dispatchChatMessageError(error);
    return undefined;
  } finally {
    rootStore.chatMessageStore.setLoading(false);
  }
};
